import React, { useState, useEffect } from "react";
import {
  MapPin,
  Clock,
  IndianRupee,
  Search,
  Share2,
  Briefcase,
} from "lucide-react";
import {
  collection,
  query,
  orderBy,
  getDocs,
  where,
  limit,
  startAfter,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import CategoryCard from "./CategoryCard";

const JobsList = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedExperience, setSelectedExperience] = useState("all");
  const [categoryCount, setCategoryCount] = useState({});
  const [lastVisibleDoc, setLastVisibleDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const jobsPerPage = 10;
  const CACHE_KEY = "cached_jobs";

  const categories = [
    "all",
    "it",
    "non-it",
    "walk-in",
    "off-campus",
    "government",
  ];
  const experienceLevels = [
    { label: "All", value: "all" },
    { label: "Freshers", value: "fresher" },
    { label: "Entry Level", value: "entry-level" },
    { label: "Mid Level", value: "mid-level" },
    { label: "Senior", value: "senior" },
    { label: "Lead", value: "lead" },
  ];

  const normalizePostedAt = (postedAt) => {
    if (!postedAt) return new Date();
    if (postedAt.toDate) return postedAt.toDate();
    if (postedAt.seconds) return new Date(postedAt.seconds * 1000);
    return new Date(postedAt);
  };

  const loadCachedJobs = () => {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return [];
    try {
      const parsed = JSON.parse(cached);
      return parsed.map((job) => ({
        ...job,
        postedDate: new Date(job.postedDate),
      }));
    } catch {
      return [];
    }
  };

  const saveJobsToCache = (jobList) => {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify(
        jobList.map((j) => ({ ...j, postedDate: j.postedDate.toISOString() }))
      )
    );
  };

  const applyFilters = (jobsList) => {
    let result = [...jobsList];

    if (selectedCategory !== "all") {
      result = result.filter((job) => job.category === selectedCategory);
    }
    if (selectedExperience !== "all") {
      result = result.filter((job) => job.experience === selectedExperience);
    }
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (job) =>
          job.title?.toLowerCase().includes(term) ||
          job.company?.toLowerCase().includes(term) ||
          job.description?.toLowerCase().includes(term) ||
          job.skills?.some((s) => s.toLowerCase().includes(term))
      );
    }

    return result;
  };

  const countCategories = (list) => {
    const counts = list.reduce((acc, job) => {
      const cat = job.category || "uncategorized";
      acc[cat] = (acc[cat] || 0) + 1;
      acc["all"] = (acc["all"] || 0) + 1;
      return acc;
    }, {});
    setCategoryCount(counts);
  };

  // Initial load fetch
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const cachedJobs = loadCachedJobs();
        if (cachedJobs.length > 0) {
          setJobs(cachedJobs);
          const filtered = applyFilters(cachedJobs);
          setFilteredJobs(filtered);
          countCategories(filtered);
          setLoading(false);
        }

        // Fetch first batch from DB
        const q = query(
          collection(db, "jobs"),
          orderBy("postedAt", "desc"),
          limit(jobsPerPage)
        );
        const snapshot = await getDocs(q);

        const fetchedJobs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          postedDate: normalizePostedAt(doc.data().postedAt),
        }));

        setLastVisibleDoc(snapshot.docs[snapshot.docs.length - 1]);
        setHasMore(snapshot.docs.length === jobsPerPage);

        // Merge cached with fetched (avoid duplicates)
        const merged = [...fetchedJobs];

        // If cache exists, merge jobs from cache that are older than last fetched job
        if (cachedJobs.length > 0) {
          const cachedOlder = cachedJobs.filter(
            (j) => new Date(j.postedDate) < new Date(fetchedJobs[0]?.postedDate)
          );
          merged.push(...cachedOlder);
        }

        const uniqueJobs = Array.from(
          new Map(merged.map((j) => [j.id, j])).values()
        );

        saveJobsToCache(uniqueJobs);
        setJobs(uniqueJobs);

        const filtered = applyFilters(uniqueJobs);
        setFilteredJobs(filtered);
        countCategories(filtered);
      } catch (err) {
        setError("Failed to load jobs. " + err.message);
        console.error(err);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  // Load more function
  const loadMoreJobs = async () => {
    if (!lastVisibleDoc) return;

    setLoadingMore(true);
    try {
      const q = query(
        collection(db, "jobs"),
        orderBy("postedAt", "desc"),
        startAfter(lastVisibleDoc),
        limit(jobsPerPage)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setHasMore(false);
        setLoadingMore(false);
        return;
      }

      const fetchedJobs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        postedDate: normalizePostedAt(doc.data().postedAt),
      }));

      setLastVisibleDoc(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === jobsPerPage);

      // Merge new jobs with existing jobs avoiding duplicates
      const combinedJobs = [...jobs];
      fetchedJobs.forEach((job) => {
        if (!combinedJobs.find((j) => j.id === job.id)) {
          combinedJobs.push(job);
        }
      });

      combinedJobs.sort(
        (a, b) => new Date(b.postedDate) - new Date(a.postedDate)
      );

      saveJobsToCache(combinedJobs);
      setJobs(combinedJobs);

      const filtered = applyFilters(combinedJobs);
      setFilteredJobs(filtered);
      countCategories(filtered);
    } catch (err) {
      setError("Failed to load more jobs. " + err.message);
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Update filtered jobs on filters change
  useEffect(() => {
    const filtered = applyFilters(jobs);
    setFilteredJobs(filtered);
  }, [searchTerm, selectedCategory, selectedExperience, jobs]);

  const formatDate = (date) => {
    const d = normalizePostedAt(date);
    const now = new Date();
    const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    if (diff < 7) return `${diff} days ago`;
    if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
    return `${Math.floor(diff / 30)} months ago`;
  };

  const formatSalary = (s) =>
    !s
      ? "Salary not disclosed"
      : typeof s === "string"
      ? s
      : `₹${Number(s).toLocaleString("en-IN", {
          maximumFractionDigits: 0,
        })}`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Link copied to clipboard!");
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (error)
    return <div className="text-red-500 text-center py-8">{error}</div>;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Categories */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        {categories.map((cat) => (
          <CategoryCard
            key={cat}
            category={cat}
            isSelected={selectedCategory === cat}
            onSelect={setSelectedCategory}
            jobCount={categoryCount[cat] || 0}
          />
        ))}
      </div>
      {/* Filters */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
        <div className="relative w-full md:w-1/2">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search jobs by title, company, or skills..."
            className="w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
          />
        </div>

        <div className="w-full md:w-60 flex flex-col">
          <label
            className="mb-1 text-sm font-medium text-gray-700 flex items-center gap-1 bg-blue-50 px-2 py-1 rounded"
            htmlFor="exp"
          >
            <Briefcase className="w-4 h-4 text-blue-600" />
            Experience
          </label>
          <select
            id="exp"
            value={selectedExperience}
            onChange={(e) => setSelectedExperience(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {experienceLevels.map((exp) => (
              <option key={exp.value} value={exp.value}>
                {exp.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Job Cards */}
      {filteredJobs.length === 0 ? (
        <div className="text-center py-12">No jobs found.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {filteredJobs.map((job) => (
            <div
              key={job.id}
              className="bg-white border p-6 rounded-xl shadow-sm hover:bg-gray-50 transition"
              onClick={() => (window.location.href = `/jobs/${job.id}`)}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                  {job.title}
                </h3>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                  {job.type || "Full-time"}
                </span>
              </div>
              <p className="text-gray-700 font-medium mb-4 line-clamp-1">
                {job.company}
              </p>
              <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-4">
                <span className="flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  {job.location || "Remote"}
                </span>
                <span className="flex items-center">
                  <IndianRupee className="w-4 h-4 mr-1" />
                  {formatSalary(job.salary)}
                </span>
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {formatDate(job.postedDate)}
                </span>
              </div>
              {job.skills && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {job.skills.slice(0, 3).map((skill, idx) => (
                    <span
                      key={idx}
                      className="bg-gray-100 px-2 py-1 text-xs rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                  {job.skills.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{job.skills.length - 3} more
                    </span>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <a
                  href={`/jobs/${job.id}`}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm flex-1 text-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  View
                </a>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const url = `${window.location.origin}/jobs/${job.id}`;
                    navigator.share
                      ? navigator.share({ title: job.title, url })
                      : copyToClipboard(url);
                  }}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2.5 rounded-lg text-sm"
                  title="Share"
                >
                  <Share2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Load More */}
      {hasMore && (
        <div className="text-center">
          <button
            onClick={loadMoreJobs}
            disabled={loadingMore}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      )}
    </div>
  );
};

export default JobsList;
